import { DateValueType } from "react-tailwindcss-datepicker";

/** Mirrors `UserActivityRequestDto`'s `@Max(90)` on `pastDays`. */
export const MAX_PAST_DAYS = 90;
/** The API's own default when no period is selected. */
export const DEFAULT_PAST_DAYS = 21;

const DAY_MS = 86_400_000;

const asDate = (value: Date | string | null | undefined): Date | null => {
	if (!value) {
		return null;
	}

	const date = value instanceof Date ? value : new Date(value);

	return Number.isNaN(date.getTime()) ? null : date;
};

export interface UserActivityWindow {
	pastDays: number;
	/** True when the selected period reaches further back than the grid can show. */
	isClamped: boolean;
}

/**
 * Maps the page's Period filter onto the User Deep Dive's day grid.
 *
 * This tab has no rows to filter — it is a fixed day axis measured against each user's
 * daily caps — so a period can only decide *how many days* the grid shows. The API caps
 * that at 90 days, so "Lifetime" and anything longer land on 90 and the UI says the view
 * was clamped rather than quietly showing a shorter window than was asked for.
 */
export function resolveUserActivityWindow(
	value: DateValueType,
	now: Date = new Date()
): UserActivityWindow {
	const start = asDate(value?.startDate);

	if (!start) {
		return { pastDays: DEFAULT_PAST_DAYS, isClamped: false };
	}

	const startOfToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
	const startOfSelection = Date.UTC(
		start.getUTCFullYear(),
		start.getUTCMonth(),
		start.getUTCDate()
	);
	// Inclusive of both ends: a period of "today" is one day, not zero.
	const days = Math.floor((startOfToday - startOfSelection) / DAY_MS) + 1;

	if (days > MAX_PAST_DAYS) {
		return { pastDays: MAX_PAST_DAYS, isClamped: true };
	}

	return { pastDays: Math.max(days, 1), isClamped: false };
}
